import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { TakeOffView, type DeskTab } from '../components/take-off-view';
import { ErrorMessage, Notice } from '../components/message';
import { SyncStatus } from '../components/sync-status';
import { isoToday } from '../lib/dates';
import { ApiError, getPricingReference, isConnectionFailure } from '../lib/api';
import { getJob, listRecords, type DeviceRecord } from '../lib/device-db';
import { useDeviceQuery, useJobRefresh } from '../lib/hooks';
import { pickOption } from '../lib/option-list';
import { addOption, currentVersion, makeFirstOption, optionColumns } from '../lib/options';
import { DEFAULT_LABOUR_TYPE, frozenOption, isFrozenVersion, jobPricesOf, parseSnapshot, toPricingRates, toSections } from '@higgins/m2q-pricing';
import { priceOption, type OptionKind, type PricedOption, type PricingRates, type SectionInfo, type SectionInput } from '@higgins/m2q-pricing';
import { loadSurfaceTypes } from '../lib/reference';
import type { PricingReferenceFromApi } from '../lib/types';
import { productIdsToPrice, useResolvedPaints } from '../lib/use-paints';
import { priceMaintenance } from '../lib/maintenance-pricing';

type ReferenceState = { reference: PricingReferenceFromApi } | { error: string } | null;

function describeFailure(err: unknown): string {
  if (isConnectionFailure(err)) return 'Pricing needs signal — the rates come from CRM. Measuring still works offline.';
  if (err instanceof ApiError) return err.message;
  return 'The rates could not be loaded.';
}

/** The branch's rates for this job on the version's pricing date. */
function usePricingReference(jobId: string, date: string | null): ReferenceState {
  const [state, setState] = useState<ReferenceState>(null);
  useEffect(() => {
    if (!date) return;
    let current = true;
    setState(null);
    getPricingReference(jobId, date).then(
      (reference) => current && setState({ reference }),
      (err: unknown) => current && setState({ error: describeFailure(err) }),
    );
    return () => {
      current = false;
    };
  }, [jobId, date]);
  return state;
}

/** The desk, under the job page's sync bar, so an estimator sees their pricing reach CRM (#445). */
export default function TakeOffPage() {
  const { jobId = '' } = useParams();
  return (
    <div>
      <SyncStatus jobId={jobId} />
      <DeskSheets />
    </div>
  );
}

/** The desk's Take Off and Summary for one measure: its areas as measured, priced at the branch's CRM rates, with the shown option's choices. */
function DeskSheets() {
  const { jobId = '', measureId = '' } = useParams();
  const [params, setParams] = useSearchParams();
  const data = useDeviceQuery(async () => ({ job: await getJob(jobId), records: await listRecords(jobId), types: await loadSurfaceTypes() }), [jobId]);
  const measure = data?.records.find((r) => r.table === 'measure' && r.id === measureId);
  const current = data ? currentVersion(data.records, measureId) : null;
  const pricingDate = typeof current?.version.fields.hig_pricingdate === 'string' ? current.version.fields.hig_pricingdate.slice(0, 10) : null;
  const referenceState = usePricingReference(jobId, pricingDate);
  useJobRefresh(data?.job?.number);

  // A measure priced for the first time gets version 1 and option A, priced at today's rates.
  useEffect(() => {
    if (measure && data && !current) void makeFirstOption(measure, isoToday()).catch(console.error);
  }, [measure, data, current]);

  const back = (
    <Link to={`/job/${jobId}`} className="flex items-center gap-1 text-sm text-slate-600">
      <ArrowLeft className="h-4 w-4" /> Areas
    </Link>
  );
  const page = (body: ReactNode) => <div className="space-y-3">{back}{body}</div>;
  if (!data) return null;
  if (!data.job || !measure) return page(<Notice>This measure isn&rsquo;t on this device. Open the job while you have signal.</Notice>);
  if (!current || !referenceState) return page(<p className="text-sm text-slate-500">Loading the branch&rsquo;s rates…</p>);
  if ('error' in referenceState) return page(<ErrorMessage>{referenceState.error}</ErrorMessage>);

  const option = pickOption(current.options, params.get('option'));
  if (!option) return page(<p className="text-sm text-slate-500">Loading the options…</p>);
  const asked = params.get('tab');
  const tab: DeskTab = asked === 'summary' || asked === 'review' || asked === 'yearly' || asked === 'scope-pa' ? asked : 'take-off';
  const show = (changes: Record<string, string>) => setParams((p) => ({ ...Object.fromEntries(p), ...changes }), { replace: true });

  return (
    <PricedTakeOff
      back={back}
      job={data.job.number}
      measure={measure}
      records={data.records}
      types={data.types.byId}
      version={current.version}
      options={current.options}
      option={option}
      reference={referenceState.reference}
      tab={tab}
      onTab={(next) => show({ tab: next })}
      onSelectOption={(id) => show({ option: id })}
      onAddOption={(kind) => void addOption(current.version, option, current.options, kind).then((id) => show({ option: id }))}
    />
  );
}

interface PricedProps {
  back: ReactNode;
  job: string;
  measure: DeviceRecord;
  records: DeviceRecord[];
  types: Awaited<ReturnType<typeof loadSurfaceTypes>>['byId'];
  version: DeviceRecord;
  options: DeviceRecord[];
  option: DeviceRecord;
  reference: PricingReferenceFromApi;
  tab: DeskTab;
  onTab: (tab: DeskTab) => void;
  onSelectOption: (id: string) => void;
  onAddOption: (kind: OptionKind) => void;
}

/** Read the option's columns without throwing during render: a newer-format option is a notice, not a crash. */
function readColumns(option: DeviceRecord) {
  try {
    return { columns: optionColumns(option) };
  } catch (err) {
    return { error: err instanceof Error ? err.message : String(err) };
  }
}

/** A frozen version's prices for the option; null for a draft; an error for a snapshot this app version can't read. */
function frozenPrices(version: DeviceRecord, optionId: string) {
  if (!isFrozenVersion(version.fields.hig_status)) return null;
  try {
    return frozenOption(parseSnapshot(version.fields.hig_ratesnapshot as string | null), optionId);
  } catch (err) {
    return { error: err instanceof Error ? err.message : String(err) };
  }
}

/**
 * A frozen version's area facts: today's names and notes, but which hours priced each area comes from the frozen
 * sections, never today's choices, so the desk shows what this version was priced from.
 */
function frozenInfo(info: Map<string, SectionInfo>, sections: readonly SectionInput[]): Map<string, SectionInfo> {
  const frozenHours = new Map(sections.map((s) => [s.id, typeof s.approvedHours === 'number']));
  return new Map([...info].map(([id, area]) => [id, { ...area, hours: undefined, approved: frozenHours.get(id) ?? false }]));
}

/** Price the shown option once its price-book paints have their prices on the pricing date. */
function PricedTakeOff({ types, ...props }: PricedProps) {
  const { back, option, reference, records, measure } = props;
  const read = useMemo(() => readColumns(option), [option]);
  const resolved = useResolvedPaints(read.columns ? productIdsToPrice(read.columns.scope) : [], reference.date);
  const notice = (text: string) => <div className="space-y-3">{back}<Notice>{text}</Notice></div>;
  if (!read.columns) return notice(read.error ?? 'This option could not be read.');
  const columns = read.columns;
  const view = (rates: PricingRates, baseRates: PricingRates, sections: SectionInput[], info: Map<string, SectionInfo>, priced: PricedOption) => (
    <TakeOffView
      {...props}
      scope={columns.scope}
      labourType={columns.labourType || DEFAULT_LABOUR_TYPE}
      rates={rates}
      baseRates={baseRates}
      sections={sections}
      info={info}
      priced={priced}
      maintenance={priceMaintenance(option, reference, columns, sections, priced, rates)}
    />
  );
  // An issued or replaced version shows the prices it was frozen at (DD, 2026-10-22), before anything is priced
  // live, so a later product, hire or job price change can't touch it; the areas' names and notes are today's.
  const frozen = frozenPrices(props.version, option.id);
  if (frozen && 'error' in frozen) return notice(frozen.error);
  if (frozen) return view(frozen.rates, frozen.rates, frozen.sections, frozenInfo(toSections(records, measure.id, types, columns.scope, reference.hireItems, frozen.rates.paints).info, frozen.sections), frozen.priced);
  if (!resolved) return <div className="space-y-3">{back}<p className="text-sm text-slate-500">Pricing the option&rsquo;s paints…</p></div>;
  if ('error' in resolved) return <div className="space-y-3">{back}<ErrorMessage>{resolved.error}</ErrorMessage></div>;
  let rates;
  let baseRates;
  try {
    const jobPrices = jobPricesOf(records, measure.id);
    rates = toPricingRates(reference, columns, resolved.products, jobPrices);
    baseRates = toPricingRates(reference, { ...columns, scope: { ...columns.scope, rates: undefined } }, resolved.products, jobPrices);
  } catch (err) {
    if (err instanceof Error) return notice(err.message);
    throw err;
  }
  const { sections, info } = toSections(records, measure.id, types, columns.scope, reference.hireItems, rates.paints);
  return view(rates, baseRates, sections, info, priceOption(sections, rates));
}
